import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmModal.tsx";
import Button from "../../components/common/Button.tsx";
import ReusableTable from "../../components/common/Table.tsx";
import {
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "material-react-table";
import {toast, ToastContainer} from 'react-toastify';
import { handleDeleteFiles, handleFetchFiles, handleDownloadErrorFiles } from "../../modules/fileListModules.ts";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { RiDeleteBin5Fill, RiDeleteBin5Line, RiEyeLine, RiSwap2Line } from "react-icons/ri";
import { MoreHoriz, MoreVert } from "@mui/icons-material";
import { showPendingToast } from "../../components/common/toastMessage.tsx";

interface Dataset {
  dataset_id: number;
  id: number;
  file_name: string;
  created_at: string;
  modified_at: string;
  total_fields: number;
  total_records: number;
  error_report: string;
  has_nulls: string;
}

const FileListModal = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const dataset_id = parseInt(location.pathname.split("/").pop() || "0");
  const templateName = location.state?.templateName || "";
  const datasetName = location.state?.datasetName || "";
  const usage = location.state?.usage
  //const toast = useToast();
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [menuAnchor, setMenuAnchor] = useState<{
    [key: number]: HTMLElement | null;
  }>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Dataset | null>(null);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-CA");
  };

  useEffect(() => {
    const getDatasets = async () => {
      setLoading(true);
      try {
        const skip = pagination.pageIndex * pagination.pageSize;
        const limit = pagination.pageSize;
        const page = pagination.pageIndex + 1;

        const response = await handleFetchFiles(dataset_id, skip, limit, page);

        let datasetsData: Dataset[] = [];
        let totalCount = 0;

        if (Array.isArray(response)) {
          datasetsData = response;
          totalCount = response.length;
        } else if (
          response &&
          typeof response === "object" &&
          "data" in response &&
          "total" in response
        ) {
          datasetsData = response.data;
          totalCount = response.total;
        }

        const formattedData = datasetsData.map((dataset: Dataset) => ({
          ...dataset,
          created_at: formatDate(dataset.created_at),
          modified_at: formatDate(dataset.modified_at),
        }));

        setDatasets(formattedData);
        setTotalRows(totalCount);
      } catch (error) {
        console.error("Error fetching datasets:", error);
        setError("Failed to fetch datasets");
      } finally {
        setLoading(false);
      }
    };

    if (dataset_id) {
      getDatasets();
    }
  }, [dataset_id]);

  const handleUpload = () => {
    navigate(`/fileUpload/${dataset_id}`, {
      state: {
        templateName: templateName,
        datasetName: datasetName,
        usage: usage
      }
    });
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: number
  ) => {
    setMenuAnchor((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleMenuClose = (id: number) => {
    setMenuAnchor((prev) => ({ ...prev, [id]: null }));
  };

  const handleViewClick = (dataset: Dataset) => {
    navigate(`/view/${dataset.id}`, {
      state: {
        datasetName: datasetName
      }
    });
    handleMenuClose(dataset.id);
  };

  const handleNullHandlingClick = (dataset: Dataset) => {
    navigate(`/nullhandling/${dataset.id}`, {
      state :{
        file: dataset,
        datasetName: datasetName.trim(),
        templateName: templateName.trim(),
        usage: usage
      }
    });
    handleMenuClose(dataset.id);
  };

  const handleDeleteClick = (dataset: Dataset) => {
    setItemToDelete(dataset);
    setIsDeleteModalOpen(true);
    handleMenuClose(dataset.id);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    try {
      await handleDeleteFiles(itemToDelete.id);

      const skip = pagination.pageIndex * pagination.pageSize;
      const limit = pagination.pageSize;
      const page = pagination.pageIndex + 1;
      const response = await handleFetchFiles(dataset_id, skip, limit, page);

      let datasetsData: Dataset[] = [];
      let totalCount = 0;

      if (Array.isArray(response)) {
        datasetsData = response;
        totalCount = response.length;
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        "total" in response
      ) {
        datasetsData = response.data;
        totalCount = response.total;
      }

      const formattedData = datasetsData.map((dataset: Dataset) => ({
        ...dataset,
        created_at: formatDate(dataset.created_at),
        modified_at: formatDate(dataset.modified_at),
      }));

      setDatasets(formattedData);
      setTotalRows(totalCount);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success("Dataset deleted successfully");
    } catch (error: any) {
      setIsDeleteModalOpen(false);
      toast.error(
        `Failed to delete ${itemToDelete.file_name}. ${error.message}`
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDownload = async (errorReportPath: string | null) => {
    if (!errorReportPath) {
      toast.error("No error report available");
      return;
    }

    try {
      const response = await handleDownloadErrorFiles(errorReportPath);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;

      const filename = errorReportPath.split("/").pop() || "error_report.csv";
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download error report");
    }
  };

    useEffect(() => {
      const timer = setTimeout(() => {
        showPendingToast();
      }, 100);
  
      return () => clearTimeout(timer);
    }, []);

  const columns = useMemo<MRT_ColumnDef<Dataset>[]>(
    () => [
      {
        accessorKey: "actions",
        header: " ",
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        size: 20,
        Cell: ({ row }) => (
          <>
            <IconButton onClick={(e) => handleMenuClick(e, row.original.id)}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={menuAnchor[row.original.id]}
              open={Boolean(menuAnchor[row.original.id])}
              onClose={() => handleMenuClose(row.original.id)}
              sx={{
                "& .MuiPaper-root": {
                  backgroundColor: "#fff",
                  width: "150px",
                },
              }}
            >
              {!row.original.error_report && (
              <MenuItem
                onClick={() => handleViewClick(row.original)}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
              >
                <RiEyeLine size={18} />&nbsp;&nbsp;View
              </MenuItem>
            )}
            {row.original.has_nulls === "True" && (
              <MenuItem
                onClick={() => handleNullHandlingClick(row.original)}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
              >
                <RiSwap2Line size={18} />&nbsp;&nbsp;Handle Nulls 
              </MenuItem>
            )}
              <MenuItem
                onClick={() => handleDeleteClick(row.original)}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
              >
                <RiDeleteBin5Line size={18} />&nbsp;&nbsp;Delete
              </MenuItem>
            </Menu>
          </>
        ),
      },
      { accessorKey: "file_name", header: "File Name" },
      { accessorKey: "created_at", header: "Date Created"},
      { accessorKey: "modified_at", header: "Date Modified" },
      { accessorKey: "total_fields", header: "No. of Headers" },
      { accessorKey: "total_records", header: "No. of Records" },
      {
        accessorKey: "error_report",
        header: "Error Report",
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.error_report ? (
            <span
              onClick={() => handleDownload(row.original.error_report)}
              className="text-blue-500 underline cursor-pointer"
            >
              Download
            </span>
          ) : (
            <span className="text-center"> - </span>
          ),
      },
    ],
    [menuAnchor]
  );

  return (
    <>
      <BreadCrumb moduleName={"Dataset"} pageName={"File List"} viewName={location?.state?.datasetName} />
      <ToastContainer position="top-center" />
      <div className="d-flex flex-sm-row flex-column w-100 justify-content-between align-items-lg-center gap-2 mb-1 mt-0">
        <div className="d-flex flex-row gap-2">
          <h5 className="rv-page-title p-2">{datasetName ? `${datasetName} - ` : ""}File List</h5>
        </div>
        <div className="d-flex align-items-center">
          <button
            type="button"
            disabled={totalRows >= 1 && usage === "p"}
            onClick={handleUpload}
            title={"Prediction dataset can have only 1 file"}
            className="btn-label rv-primary-btn me-2 rounded-1 px-4 py-1 text-nowrap btn btn-secondary"
          >
            <i className="ri-add-line label-icon align-middle fs-16 me-1"></i>
            <span className="ps-3 ms-2 me-1">Upload File</span>
          </button>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemType="file"
        itemName={itemToDelete?.file_name}
        loading={deleteLoading}
      />

      <div className="container-fluid px-0">
        <div className="disable-margin-card table-responsive table-customer border table-border w-100">
          <ReusableTable
            columns={columns}
            data={datasets}
            loading={loading}
            error={error}
            pagination={pagination}
            onPaginationChange={setPagination}
          />
        </div>
      </div>
    </>
  );
};

export default FileListModal;